import {
  changeVariablePosition,
  editVariable,
  JSONToValue,
  removeVariable,
  VariableInsertionPosition,
  VariableModel,
} from '@follow/farte'
import {
  EditorVariableDisplayConfig,
  FeaturesType,
  getDisplayedName,
  VariableKind,
  VariableKindPrefix,
} from '@follow/cdk'
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDebounce, useToggle } from 'react-use'
import { v4 as uuid } from 'uuid'
import { FullLoader, RoundedButton } from '../../../shared'
import AddContentToDocumentEditor from '../../../../containers/AddContentToDocumentEditor'
import DocumentEditor from '../../../../containers/DocumentEditor'
import { HotKeys, useCurrentPatient, useHotKeyEffect } from '../../../../hooks/utils'
import { invokeAfter } from '../../../../misc/aspect.utilities'
import { FwTrackingEvent, TrackingService } from '../../../../misc/Tracking'
import {
  LightQuestionnaire,
  Question,
  QuestionInstance,
  QuestionType,
} from '../../../../model/Questionnaire'
import { SelectedContentType } from '../../../../store/ui/medicalEvents/medicalEventContent'
import { QuestionnaireInstance } from '../QuestionnaireInstance'
import { DocumentContactsForm } from './DocumentContactsForm'
import { PatientRecipient } from './DocumentContactsForm/DocumentContactsForm.model'
import { DocumentInstanceContentProps } from './DocumentInstanceContent.model'
import styles from './DocumentInstanceContent.module.scss'
import { DocumentLayoutForm } from './DocumentLayoutForm'
import { canAddContentToDocument } from '../../../../misc/documents.utilities'
import { RecipientContact } from '../../../../model/Contact'
import {
  computeManualPrescriptionVariableId,
  computePrescriptionVariableId,
} from '../../../../misc/drug.utilities'
import { Block } from 'slate'
import { BizoneMarker, DocumentCategoryKeys } from '../../../../model/DocumentCategory'
import { isDefined } from '../../../../misc/functions.utilities'
import { DocumentPosology } from '../../../document'
import { BottomPanelComponentType } from '../../../../store/ui/bottomPanel'
import { ManualPrescriptionBottomPanel, SamAlertModal } from '../../../drug'
import { DocumentInstanceBanners } from './DocumentInstanceBanners'
import { DocumentTemplateEditorVariantType } from '../../../../model/DocumentTemplate'
import { GridLayoutDocument } from '../../../documentLayout'
import { SearchQuestionnaireBottomPanel } from '../../../shared/bottomPanel/SearchQuestionnaireBottomPanel'
import { SearchQuestionBottomPanel } from '../../../shared/bottomPanel/SearchQuestionBottomPanel'
import { EditQuestionInstanceBottomPanel } from '../../../shared/bottomPanel/EditQuestionInstanceBottomPanel'
import { Acte, SelectedQuoteLinePayload } from '../../../../model/Acte'
import { QuoteLineForm } from '../../../ccam'
import { SearchActeBottomPanel } from '../../../shared/bottomPanel/SearchActeBottomPanel'
import { useDownloadDocuments, usePrintDocuments } from '../../../../hooks/queries/document'
import { FarteDocumentInstance } from '../../../../model/DocumentInstance'
import { useUserEnabledFeature } from '../../../../hooks/utils/user'
import { PrescriptionVariable } from '../../../../model/Prescription'
import { SearchDrug } from '../../../drug/SearchDrug'
import { noop } from 'lodash'
import { ManualPrescription } from '../../../../model/ManualPrescription'
import { TaskList } from '../../../task/TaskList'
import { useCreateAudiometerInstance } from '../../../../hooks/queries/audiometry'
import { computeAudiometerVariableId } from '../../../../misc/audiometry'
import { AudiometerInstanceForm } from '../../../audiometry'
import { defaultQuestionLabels } from '../../../../model/QuestionnaireShared'
import { useGetAllDocumentLayouts } from '../../../../hooks/queries/documentLayouts'

interface SearchDrugState {
  allowMultiSelect: boolean
  onSelectDrug: (prescriptionBase: PrescriptionVariable) => void
}

function mapContactToString(contact: RecipientContact) {
  return `${contact.title} ${contact.firstName} ${contact.familyName}`
}

export const DocumentInstanceContent: FunctionComponent<DocumentInstanceContentProps> = ({
  isDocumentHistoryOpen: autoSaveEnabled,
  documentInstance,
  medicalEvent,
  questionnaire,
  prescription,
  quoteLine,
  audiometer,
  documentAlerts,
  displayableSamAlerts,
  vidalInfos,
  severityLabels,
  variablesData,
  addExistingQuestionToInUseDocumentInstance,
  addQuestionnaireToInUseDocumentInstance,
  addQuestionToInUseDocumentInstance,
  createDocumentHistoryRevision,
  onSelectionChange,
  documentQuestionList,
  searchBaseVariable,
  addQuoteLine,
  searchDocumentHistory,
  selectedContent,
  setSelectedContent,
  updateDocumentInstanceValue,
  updateDocumentInstanceThenRefetchVariableData,
  copyHtmlToClipboard,
  setInUseMedicalEvent,
  setBottomPanelOptions,
  getDocumentAlerts,
  lockDocument,
  disableCurrentSamAlerts,
  acknowledgeCurrentSamAlerts,
  getVariableData,
  setVariableData,
  refetchDocument,
  addError,
}) => {
  const [editQuestionId, setEditQuestionId] = useState<number | null>(null)
  const [localEditorValue, setLocalEditorValue] = useState(JSONToValue(documentInstance.template))
  const [displaySamAlertsModal, setDisplaySamAlertsModal] = useState(false)
  const [isOpenSearchQuestionnaireBottomPanel, toggleOpenSearchQuestionnaireBottomPanel] =
    useToggle(false)
  const [isOpenSearchQuestionBottomPanel, toggleOpenSearchQuestionBottomPanel] = useToggle(false)
  const [isOpenSearchActeBottomPanel, toggleSearchActeBottomPanel] = useToggle(false)
  const [searchDrugState, setSearchDrugState] = useState<SearchDrugState>()
  const [onSelectQuestionnaireCallback, setOnSelectQuestionnaireCallback] = useState(
    () => (_item: object) => {},
  )
  const [onSelectQuestionCallback, setOnSelectQuestionCallback] = useState(
    () => (_item: object) => {},
  )
  const [onSelectActeCallback, setOnSelectActeCallback] = useState(() => (_item: object) => {})
  const [onSelectManualPrescriptionCallback, setOnSelectManualPrescriptionCallback] =
    useState<(variable: ManualPrescription) => void | undefined>()

  const isLapActive = useUserEnabledFeature(FeaturesType.drug)

  const forceVariableDataFetchAfterNextUpdate = useRef(false)
  const forceReinitTemplate = useRef(false)
  // Ne doit être positionné à true que par une vraie édition utilisateur (pas par le montage
  // initial ni par une resynchronisation interne), pour éviter un autosave "fantôme" déclenché
  // par la simple normalisation Slate du template au premier rendu (cf. JSONToValue plus haut).
  const hasUserEditedRef = useRef(false)

  const handleLocalEditorValueChange: Dispatch<SetStateAction<typeof localEditorValue>> =
    useCallback((value) => {
      hasUserEditedRef.current = true
      setLocalEditorValue(value)
    }, [])

  const { currentPatient } = useCurrentPatient()

  const { mutate: createAudiometerInstance } = useCreateAudiometerInstance()

  const setSelectedInterval = useCallback(
    (intervalId: string | undefined) => {
      if (prescription) {
        setSelectedContent({
          type: SelectedContentType.PRESCRIPTION,
          prescriptionId: prescription.uuid,
          intervalId,
        })
      }
    },
    [prescription, setSelectedContent],
  )
  const { print } = usePrintDocuments()
  const { download } = useDownloadDocuments()

  useHotKeyEffect(
    HotKeys.ModP,
    (event) => {
      event.preventDefault()

      print({
        documents: [
          {
            id: documentInstance.id,
            category: documentInstance.category.id,
            type: documentInstance.type,
          },
        ],
      })
    },
    [documentInstance.id],
  )

  useHotKeyEffect(
    HotKeys.ModShiftS,
    (event) => {
      event.preventDefault()
      download({
        documents: [
          {
            id: documentInstance.id,
            category: documentInstance.category.id,
            type: documentInstance.type,
          },
        ],
      })
    },
    [documentInstance.id],
  )

  useHotKeyEffect(
    HotKeys.ModShitV,
    (event) => {
      event.preventDefault()
      setSelectedContent({
        type: SelectedContentType.ADD_VARIABLE_PANEL,
      })
    },
    [documentInstance.id],
  )

  useEffect(() => {
    getDocumentAlerts()
    return () => {
      setVariableData(null)
    }
  }, [getDocumentAlerts, setVariableData])

  useEffect(() => {
    if (variablesData === null && selectedContent.type === SelectedContentType.EDITOR) {
      getVariableData()
    }
    if (selectedContent.type !== SelectedContentType.EDITOR) {
      setVariableData(null)
    }
  }, [selectedContent.type, variablesData, getVariableData, setVariableData])

  useEffect(() => {
    if (!autoSaveEnabled) {
      hasUserEditedRef.current = false
      setLocalEditorValue(JSONToValue(documentInstance.template))
    }
  }, [autoSaveEnabled, documentInstance.locked, documentInstance.template])

  // Création de révision lorsque l'utilisateur quitte l'éditeur
  useEffect(() => {
    return () => {
      if (!documentInstance.locked) {
        createDocumentHistoryRevision(documentInstance.id, 'documents')
      } else {
        console.log({ msg: 'no history, doc locked' })
      }
    }
  }, [createDocumentHistoryRevision, documentInstance.id, documentInstance.locked])

  // Mise à jour du prix d'un document dans l'objet medical event après une édition
  useEffect(() => {
    if (isDefined(medicalEvent)) {
      const currentDocIndex = medicalEvent.documents.findIndex(
        ({ id }) => id === documentInstance.id,
      )
      const currentDoc = medicalEvent.documents[currentDocIndex]
      if (currentDocIndex > -1 && currentDoc.price !== documentInstance.price) {
        const { documents } = medicalEvent
        documents[currentDocIndex].price = documentInstance.price

        setInUseMedicalEvent({
          ...medicalEvent,
          documents,
        })
      }
    }
  }, [medicalEvent, documentInstance.price, documentInstance.id, setInUseMedicalEvent])

  useDebounce(
    () => {
      if (
        autoSaveEnabled &&
        hasUserEditedRef.current &&
        JSON.stringify(documentInstance.template) !== JSON.stringify(localEditorValue) &&
        !disabled
      ) {
        const successCallback = forceReinitTemplate.current
          ? (newDocumentInstance: FarteDocumentInstance) => {
              hasUserEditedRef.current = false
              setLocalEditorValue(JSONToValue(newDocumentInstance.template))
            }
          : undefined

        updateDocumentInstanceValue(
          documentInstance.id,
          localEditorValue,
          forceVariableDataFetchAfterNextUpdate.current,
          successCallback,
        )

        hasUserEditedRef.current = false

        if (forceVariableDataFetchAfterNextUpdate.current) {
          forceVariableDataFetchAfterNextUpdate.current = false
        }

        if (forceReinitTemplate.current) {
          forceReinitTemplate.current = false
        }
      }
    },
    500,
    [autoSaveEnabled, localEditorValue],
  )

  // Mise à jour de la valeur locale de l'éditeur quand une variable est supprimée hors de l'éditeur (i.e. via les chips)
  useEffect(() => {
    if (autoSaveEnabled) {
      setLocalEditorValue((currentLocalEditorValue) => {
        if (
          variablesData === null &&
          JSON.stringify(documentInstance.template) !== JSON.stringify(currentLocalEditorValue)
        ) {
          hasUserEditedRef.current = false
          return JSONToValue(documentInstance.template)
        }
        return currentLocalEditorValue
      })
    }
  }, [variablesData, autoSaveEnabled, documentInstance.template])

  useEffect(() => {
    setDisplaySamAlertsModal(displayableSamAlerts.length > 0)
  }, [displayableSamAlerts.length])

  const selectEditorTab = () => {
    setSelectedContent({
      type: SelectedContentType.EDITOR,
    })
  }

  const handleCreateQuestion = (
    type: QuestionType,
    onVariableCreated: (question: Question) => void,
  ) => {
    const onQuestionCreated = (question: Question) => {
      // Notifie l'editeur de l'insertion d'une nouvelle variable
      onVariableCreated(question)
      setEditQuestionId(question.id)
    }
    addQuestionToInUseDocumentInstance(
      documentInstance.id,
      type,
      defaultQuestionLabels[type],
      onQuestionCreated,
    )
  }

  const handleSelectVariableDisplayConfig = (
    farteKey: string,
    variableId: string,
    displayConfig?: string,
  ) => {
    // /!\ Comportement très similaire à DocumentTemplateEditorContent.component.tsx
    // Edition de la variable
    const variableContext = displayConfig
      ? {
          displayConfig,
        }
      : undefined
    const newLocalEditorValue = editVariable(localEditorValue, farteKey, {
      variableId,
      variableContext,
    })
    // Modification de l'état local de l'editeur
    handleLocalEditorValueChange(newLocalEditorValue)
    forceVariableDataFetchAfterNextUpdate.current = true
  }

  const handleVariablePosition = useCallback(
    (
      variableId: string,
      position?: VariableInsertionPosition,
      relativeBlock?: Block,
      removeBlock?: boolean,
    ) => {
      const newLocalEditorValue = changeVariablePosition(
        localEditorValue,
        variableId,
        position,
        relativeBlock,
        removeBlock,
      )
      // Modification de l'état local de l'editeur
      handleLocalEditorValueChange(newLocalEditorValue)
    },
    [localEditorValue, handleLocalEditorValueChange],
  )

  const handleRemoveVariable = useCallback(
    (variableId: string) => {
      const newLocalValue = removeVariable(localEditorValue, variableId)
      // Modification de l'état local de l'editeur
      handleLocalEditorValueChange(newLocalValue)
    },
    [localEditorValue, handleLocalEditorValueChange],
  )

  const handleInsertVariable = (
    type: VariableKind,
    onVariableCreated: (
      id: string,
      variableContext?: Record<string, string>,
      position?: VariableInsertionPosition,
      relativeBlock?: Block,
    ) => void,
  ) => {
    switch (type) {
      case VariableKind.Base:
        return searchBaseVariable(invokeAfter(onVariableCreated, selectEditorTab), 'document')
      case VariableKind.Drug:
      case VariableKind.MultiDrugs:
        setSearchDrugState({
          allowMultiSelect: type === VariableKind.MultiDrugs,
          onSelectDrug: (selected) => {
            if (selected.drugs.length < 1) return

            if (
              documentInstance.category.id === DocumentCategoryKeys.BizonePrescriptionForMedication
            ) {
              onVariableCreated(
                computePrescriptionVariableId(selected.prescriptionVariableUuid),
                undefined,
                VariableInsertionPosition.START,
                localEditorValue.document
                  .getBlocks()
                  .find((block) =>
                    selected.isAld
                      ? block?.data.get('marker') === BizoneMarker.ALD
                      : block?.data.get('marker') === BizoneMarker.NON_ALD,
                  ),
              )
            } else {
              onVariableCreated(computePrescriptionVariableId(selected.prescriptionVariableUuid))
            }
            selectEditorTab()
            setSearchDrugState(undefined)
          },
        })
        break
      case VariableKind.ManualPrescription:
        setOnSelectManualPrescriptionCallback(() => (manualPrescription: ManualPrescription) => {
          if (
            documentInstance.category.id === DocumentCategoryKeys.BizonePrescriptionForMedication
          ) {
            onVariableCreated(
              computeManualPrescriptionVariableId(manualPrescription.variableUuid),
              undefined,
              VariableInsertionPosition.START,
              localEditorValue.document
                .getBlocks()
                .find((block) => block?.data.get('marker') === BizoneMarker.NON_ALD),
            )
          } else {
            onVariableCreated(computeManualPrescriptionVariableId(manualPrescription.variableUuid))
          }

          setOnSelectManualPrescriptionCallback(undefined)
          selectEditorTab()
        })
        break
      case VariableKind.Question:
        toggleOpenSearchQuestionBottomPanel()
        setOnSelectQuestionCallback(() => selectQuestionCallback(onVariableCreated))
        break
      case VariableKind.Questionnaire:
        toggleOpenSearchQuestionnaireBottomPanel()
        setOnSelectQuestionnaireCallback(() => selectQuestionnaireCallback(onVariableCreated))
        break
      case VariableKind.Acte:
        toggleSearchActeBottomPanel()
        setOnSelectActeCallback(() => selectActeCallback(onVariableCreated))
        break
      case VariableKind.Audiometer:
        createAudiometerInstance(
          { documentId: documentInstance.id },
          {
            onSuccess: (data) => {
              onVariableCreated(computeAudiometerVariableId(data))
              selectEditorTab()
            },
          },
        )
    }
  }

  const selectQuestionCallback =
    (
      onVariableCreated: (
        id: string,
        variableContext?: Record<string, string>,
        position?: VariableInsertionPosition,
        relativeBlock?: Block,
      ) => void,
    ) =>
    (question: Question) => {
      addExistingQuestionToInUseDocumentInstance(
        documentInstance.id,
        question,
        invokeAfter(
          ({ id }) => onVariableCreated(`${VariableKindPrefix.QUESTION}_${id}`),
          selectEditorTab,
        ),
      )
      toggleOpenSearchQuestionBottomPanel()
    }

  const selectQuestionnaireCallback =
    (
      onVariableCreated: (
        id: string,
        variableContext?: Record<string, string>,
        position?: VariableInsertionPosition,
        relativeBlock?: Block,
      ) => void,
    ) =>
    (questionnaire: LightQuestionnaire) => {
      addQuestionnaireToInUseDocumentInstance(
        documentInstance.id,
        questionnaire,
        invokeAfter(({ id, scoreConfig }) => {
          const variableContext = {
            displayConfig: scoreConfig
              ? EditorVariableDisplayConfig.TITLE_AND_SCORE_VALUE
              : EditorVariableDisplayConfig.FULL,
          }
          const { selection, document: farteDocument } = localEditorValue
          // L'insertion de questionnaire se fasse à la position courante du curseur
          // Sauf si le curseur est au début et dans ce cas c'est inséré à la fin.
          const position =
            selection.isCollapsed &&
            (selection.start.isAtStartOfNode(farteDocument) ||
              selection.end.isAtEndOfNode(farteDocument))
              ? VariableInsertionPosition.END
              : undefined
          return onVariableCreated(
            `${VariableKindPrefix.QUESTIONNAIRE}_${id}`,
            variableContext,
            position,
          )
        }, selectEditorTab),
      )
      toggleOpenSearchQuestionnaireBottomPanel()
    }

  const selectActeCallback =
    (
      onVariableCreated: (
        id: string,
        variableContext?: Record<string, string>,
        position?: VariableInsertionPosition,
        relativeBlock?: Block,
      ) => void,
    ) =>
    (acte: Acte) => {
      const variableUuid = uuid()
      const quoteLinePayload: SelectedQuoteLinePayload = { codeActe: acte.codeActe, variableUuid }
      addQuoteLine(
        quoteLinePayload,
        invokeAfter(
          () =>
            onVariableCreated(`${VariableKindPrefix.QUOTE_LINE}_${quoteLinePayload.variableUuid}`),
          selectEditorTab,
        ),
      )
      toggleSearchActeBottomPanel()
    }

  const handleEditQuestion = (question: QuestionInstance) => {
    setEditQuestionId(question.id)
  }

  const handleQuestionEdited = () => {
    refetchDocument(documentInstance.id)
    getVariableData()
  }

  const handleOnPaste = (variables: VariableModel[]) => {
    if (variables.length) {
      forceReinitTemplate.current = true
    }
  }

  function handleChangeDocumentInstanceContacts(contacts: ReadonlyArray<RecipientContact>) {
    updateDocumentInstanceThenRefetchVariableData(documentInstance.id, {
      contacts,
      type: documentInstance.type,
    })
  }

  function handleChangePatientRecipient(updates: PatientRecipient) {
    updateDocumentInstanceThenRefetchVariableData(documentInstance.id, {
      ...updates,
      type: documentInstance.type,
    })
  }

  const openAlertsPanel = useCallback(() => {
    setBottomPanelOptions({
      open: true,
      componentType: BottomPanelComponentType.DocumentAlerts,
      title: 'Aide à la prescription : Signaux et Alertes',
      testId: 'bottom_panel-document_alerts-search',
    })
  }, [setBottomPanelOptions])

  const handleCloseSamModal = useCallback(
    (disabledAlerts: boolean) => {
      setDisplaySamAlertsModal(false)
      acknowledgeCurrentSamAlerts()
      if (disabledAlerts) {
        disableCurrentSamAlerts()
      }
    },
    [acknowledgeCurrentSamAlerts, disableCurrentSamAlerts],
  )

  const handleUnlockDocument = () => {
    lockDocument(documentInstance.id, false)
  }

  const disabled = !medicalEvent?.isEditable || documentInstance.locked

  const [selectedTab, setSelectedTab] = useState<'tasks' | 'layout' | 'contacts' | undefined>(
    undefined,
  )

  const { documentLayoutList } = useGetAllDocumentLayouts()

  const selectedLayout = useMemo(() => {
    return documentLayoutList.find((layout) => layout.id === documentInstance.documentLayoutId)
  }, [documentInstance.documentLayoutId, documentLayoutList])

  const selectedLayoutTitle = selectedLayout ? selectedLayout.title : ''

  const { recipientsLabel, recipientsCount } = useMemo(() => {
    const copyContacts = documentInstance.contacts.filter(({ copyRecipient }) => copyRecipient)
    const mainContacts = documentInstance.contacts.filter(({ mainRecipient }) => mainRecipient)

    let main = mainContacts.map(mapContactToString).join(', ')
    let copy = copyContacts.map(mapContactToString).join(', ')

    let count = mainContacts.length + copyContacts.length

    if (currentPatient) {
      const { firstname, lastname } = getDisplayedName(currentPatient)
      const patientFullname = `${firstname} ${lastname}`
      if (documentInstance.patientAsCopyRecipient) {
        copy = `${copy ? `${copy}, ` : ``}${patientFullname}`
        count++
      }
      if (documentInstance.patientAsMainRecipient) {
        main = `${main ? `${main}, ` : ``}${patientFullname}`
        count++
      }
    }

    if (
      copyContacts.length === 0 &&
      mainContacts.length === 0 &&
      !documentInstance.patientAsCopyRecipient &&
      !documentInstance.patientAsMainRecipient
    ) {
      return {
        recipientsLabel: 'Aucun destinataire',
        recipientsCount: 0,
      }
    }

    const recipientsLabel = `${main ? `${main}${copy ? ` ` : ``}` : ``}${copy ? `(en copie : ${copy})` : ``}`
    return {
      recipientsLabel,
      recipientsCount: count,
    }
  }, [
    currentPatient,
    documentInstance.contacts,
    documentInstance.patientAsCopyRecipient,
    documentInstance.patientAsMainRecipient,
  ])

  const truncatedRecipientsLabel = useMemo(() => {
    return recipientsLabel.length > 25 ? recipientsLabel.slice(0, 25) + '...' : recipientsLabel
  }, [recipientsLabel])

  return (
    <div className={styles.documentInstanceWithBanners}>
      <DocumentInstanceBanners
        document={documentInstance}
        isEventLocked={!!medicalEvent?.locked}
        selectedContent={selectedContent.type}
        alerts={documentAlerts}
        alertSeverityLabels={severityLabels}
        vidalInfos={vidalInfos}
        onUnlockDocument={handleUnlockDocument}
        onOpenAlertsPanel={openAlertsPanel}
      />
      <div className={styles.documentInstanceContainer}>
        {selectedContent.type === SelectedContentType.TASK && currentPatient && (
          <div className={styles.taskContainer}>
            <TaskList
              patient={currentPatient}
              document={documentInstance}
              contentEmptyState="Vous n’avez actuellement aucune tâche à réaliser pour ce document."
              disableAccordion
            />
          </div>
        )}
        {selectedContent.type === SelectedContentType.EDITOR &&
          (variablesData === null ? (
            <FullLoader />
          ) : (
            <>
              <GridLayoutDocument rowsTemplate="repeat(3, auto)" width="100%" gap="extraSmall">
                <div className={styles.tabsHeader}>
                  {documentInstance.contentType.BASE_VARIABLE_ADD && (
                    <RoundedButton
                      theme={selectedTab === 'contacts' ? 'primary' : 'light'}
                      label={`Destinataires : ${truncatedRecipientsLabel ? ` (${recipientsCount}) : ${truncatedRecipientsLabel}` : 'Aucun'}`}
                      onClick={() =>
                        setSelectedTab((prev) => (prev === 'contacts' ? undefined : 'contacts'))
                      }
                    />
                  )}
                  <RoundedButton
                    theme={selectedTab === 'layout' ? 'primary' : 'light'}
                    label={`Mise en page : ${selectedLayoutTitle ? `${selectedLayoutTitle}` : 'Aucune'}`}
                    onClick={() =>
                      setSelectedTab((prev) => (prev === 'layout' ? undefined : 'layout'))
                    }
                  />
                </div>

                <div className={styles.tabContent}>
                  {selectedTab === 'layout' && (
                    <DocumentLayoutForm documentInstance={documentInstance} isEditable />
                  )}

                  {selectedTab === 'contacts' && documentInstance.contentType.BASE_VARIABLE_ADD && (
                    <DocumentContactsForm
                      addError={addError}
                      contacts={documentInstance.contacts}
                      isEditable={!disabled}
                      onChangeContacts={handleChangeDocumentInstanceContacts}
                      onChangePatientRecipient={handleChangePatientRecipient}
                      patientAsCopyRecipient={documentInstance.patientAsCopyRecipient}
                      patientAsMainRecipient={documentInstance.patientAsMainRecipient}
                    />
                  )}
                </div>

                <DocumentEditor
                  onPaste={handleOnPaste}
                  enableVariableFixedValues={false} // Désactivé le temps que la migration des fixed values soit faite, voir https://fwhealth.atlassian.net/browse/DEV-3466
                  editableDocument={documentInstance}
                  onEditQuestion={handleEditQuestion}
                  onSelectVariableDisplayConfig={handleSelectVariableDisplayConfig}
                  onChangeVariablePosition={handleVariablePosition}
                  onRemoveVariable={handleRemoveVariable}
                  onClickAddVariable={
                    canAddContentToDocument(documentInstance.contentType, isLapActive)
                      ? () =>
                          onSelectionChange({
                            type: DocumentTemplateEditorVariantType.ADD_VARIABLE_PANEL,
                          })
                      : undefined
                  }
                  onClickHistory={() => {
                    TrackingService.sendEvent(FwTrackingEvent.SHOW_DOCUMENT_HISTORY, {
                      id: documentInstance.id,
                      type: 'documents',
                    })
                    return searchDocumentHistory(
                      documentInstance.id,
                      documentInstance.hash,
                      'documents',
                    )
                  }}
                  value={localEditorValue}
                  variablesData={variablesData ?? {}}
                  setValue={handleLocalEditorValueChange}
                  isReadonly={disabled || !documentInstance.contentType.TEXT_INPUT}
                  copyHtmlToClipboard={copyHtmlToClipboard}
                />
              </GridLayoutDocument>
            </>
          ))}
        {selectedContent.type === SelectedContentType.QUESTIONNAIRE && questionnaire && (
          <QuestionnaireInstance
            key={selectedContent.questionnaireId}
            refreshDocument={handleQuestionEdited}
            documentInstanceId={documentInstance.id}
            questions={questionnaire.questions}
            isReadonly={!questionnaire?.isEditable}
            disabled={disabled}
          />
        )}
        {selectedContent.type === SelectedContentType.VARIABLES && documentQuestionList && (
          <QuestionnaireInstance
            refreshDocument={handleQuestionEdited}
            documentInstanceId={documentInstance.id}
            questions={documentQuestionList.questions}
            disabled={disabled}
            isVariable
          />
        )}
        {selectedContent.type === SelectedContentType.PRESCRIPTION && prescription && (
          <DocumentPosology
            prescription={prescription}
            activeIntervalId={selectedContent.intervalId}
            setActiveIntervalId={setSelectedInterval}
            disabled={disabled}
            onSuccessfullUpdate={selectEditorTab}
          />
        )}
        {selectedContent.type === SelectedContentType.ADD_VARIABLE_PANEL && (
          <AddContentToDocumentEditor
            value={localEditorValue}
            contentType={documentInstance.contentType}
            setValue={handleLocalEditorValueChange}
            onCreateQuestion={handleCreateQuestion}
            onInsertVariable={handleInsertVariable}
            tracker={FwTrackingEvent.MEDICAL_EVENT_VARIABLE_ADD}
          />
        )}
        {selectedContent.type === SelectedContentType.ACTE && quoteLine && (
          <QuoteLineForm
            quoteLine={quoteLine}
            documentInstanceId={documentInstance.id}
            key={quoteLine.id}
          />
        )}
        {selectedContent.type === SelectedContentType.AUDIOMETER && audiometer && (
          <AudiometerInstanceForm
            initialAudiometer={audiometer}
            key={audiometer.id}
            disabled={disabled}
          />
        )}
      </div>
      <SamAlertModal
        display={displaySamAlertsModal}
        alerts={displayableSamAlerts}
        onClose={handleCloseSamModal}
      />
      <SearchQuestionnaireBottomPanel
        display={isOpenSearchQuestionnaireBottomPanel}
        onRequestClose={toggleOpenSearchQuestionnaireBottomPanel}
        onSelect={onSelectQuestionnaireCallback}
      />
      <SearchQuestionBottomPanel
        display={isOpenSearchQuestionBottomPanel}
        onRequestClose={toggleOpenSearchQuestionBottomPanel}
        onSelect={onSelectQuestionCallback}
      />
      <SearchActeBottomPanel
        display={isOpenSearchActeBottomPanel}
        onRequestClose={toggleSearchActeBottomPanel}
        onSelect={onSelectActeCallback}
      />
      <EditQuestionInstanceBottomPanel
        display={isDefined(editQuestionId)}
        onRequestClose={() => setEditQuestionId(null)}
        itemId={editQuestionId}
        onItemEdited={handleQuestionEdited}
      />
      <SearchDrug
        onSelect={searchDrugState?.onSelectDrug ?? noop}
        display={isDefined(searchDrugState)}
        onClose={() => setSearchDrugState(undefined)}
        isMultiSelect={searchDrugState?.allowMultiSelect}
        updatePendingVariables
        label={
          searchDrugState?.allowMultiSelect
            ? 'Ajouter une préparation de médicaments'
            : 'Ajouter un médicament'
        }
      />
      <ManualPrescriptionBottomPanel
        display={isDefined(onSelectManualPrescriptionCallback)}
        onClose={() => setOnSelectManualPrescriptionCallback(undefined)}
        onSubmit={onSelectManualPrescriptionCallback ?? noop}
      />
    </div>
  )
}
